import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Heading, Text, Tooltip } from '@modusign/design-system';

import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';

import { Signer } from '../../types/ExternalDocument';
import formatInternationalPhoneNumber from '../../utils/formatInternationalPhoneNumber';
import DetailInfoItem from '../DetailInfoItem';

interface ParticipantsViewerProps {
  externalDocumentId: string;
  participants: Signer[];
  onClickEdit: () => void;
}

export default function ParticipantsViewer({
  externalDocumentId,
  participants,
  onClickEdit,
}: ParticipantsViewerProps) {
  const { updatePermission } = useExternalDocumentPermission(externalDocumentId);

  return (
    <Box>
      {participants.length > 0 ? (
        <Box as="ol">
          {participants.map((participant, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ParticipantViewer index={index} key={index} participant={participant} />
          ))}
        </Box>
      ) : (
        <Flex alignItems="center" flexDirection="column" height="167px" justifyContent="center">
          <Box color="textNeutralLight" fontSize="18px">
            <FontAwesomeIcon icon={faUserGroup} />
          </Box>
          <Text mt={3}>서명자를 등록해 주세요.</Text>
        </Flex>
      )}
      <Flex
        alignItems="center"
        flexDirection="row-reverse"
        justifyContent="space-between"
        mb={[10, null, 0]}
        mt={9}
      >
        <Tooltip
          content={updatePermission.message}
          disabled={updatePermission.isPermitted}
          placement="top"
        >
          <Button disabled={!updatePermission.isPermitted} type="button" onClick={onClickEdit}>
            수정하기
          </Button>
        </Tooltip>
      </Flex>
    </Box>
  );
}

interface ParticipantViewerProps {
  index: number;
  participant: Signer;
}

function ParticipantViewer({ index, participant }: ParticipantViewerProps) {
  return (
    <Box as="li" mt={7}>
      <Heading as="div" fontSize="sm" fontWeight="medium">
        {index + 1}
      </Heading>
      <Flex
        borderTop="1px solid"
        borderTopColor="lightGray300"
        flexDirection="column"
        minHeight="60px"
        mt={4}
      >
        {participant.type === 'INDIVIDUAL' ? (
          <DetailInfoItem name="개인">
            <Text fontWeight="medium">{participant.name}</Text>
            <Text color="textNeutralLight" fontSize="sm" ml={1}>
              {[
                participant.email,
                participant.phone ? formatInternationalPhoneNumber(participant.phone) : '',
              ]
                .filter(Boolean)
                .join(' / ')}
            </Text>
            {!participant.externalSigner && (
              <Text
                color="textNeutralLight"
                fontSize="xs"
                fontWeight="medium"
                ml={3}
                sx={{ borderRadius: '3px', bg: 'statusBackgroundNeutralBase', py: '2px', px: 2 }}
              >
                우리측
              </Text>
            )}
          </DetailInfoItem>
        ) : (
          <>
            <DetailInfoItem name="회사">
              <Flex flexDirection="column">
                <div>
                  <Text fontWeight="medium">{participant.company}</Text>
                  <Text color="textNeutralLight" fontSize="sm" ml={1}>
                    {[
                      participant.email,
                      participant.phone ? formatInternationalPhoneNumber(participant.phone) : '',
                    ]
                      .filter(Boolean)
                      .join(' / ')}
                  </Text>
                  {!participant.externalSigner && (
                    <Text
                      color="textNeutralLight"
                      fontSize="xs"
                      fontWeight="medium"
                      ml={3}
                      sx={{
                        borderRadius: '3px',
                        bg: 'statusBackgroundNeutralBase',
                        py: '2px',
                        px: 2,
                      }}
                    >
                      우리측
                    </Text>
                  )}
                </div>
                <Box ml="10px" mt="2px">
                  <Text color="textNeutralLight" fontSize="sm">
                    서명자 : {participant.name}
                  </Text>
                </Box>
              </Flex>
            </DetailInfoItem>
            {participant.personInCharge && (
              <DetailInfoItem name="담당자">
                <Text>{participant.personInCharge.name}</Text>
                <Text color="textNeutralLight" fontSize="sm" ml={1}>
                  {[
                    participant.personInCharge.email,
                    participant.personInCharge.phone
                      ? formatInternationalPhoneNumber(participant.personInCharge.phone)
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' / ')}
                </Text>
              </DetailInfoItem>
            )}
          </>
        )}
      </Flex>
    </Box>
  );
}
